import { useMutation } from "@tanstack/react-query";

import { API_URL } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast } from "@/utils/toast";

export interface ExportPromptVersionRequest {
  workspaceId: number;
  promptRegistryId: number;
  versionNumber: number;
}

export interface ExportPromptVersionResponse {
  prompt: { id: number; name: string };
  version: { id: number; number: number };
}

const downloadJson = (data: unknown, filename: string) => {
  const blobUrl = URL.createObjectURL(
    new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }),
  );
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
};

export const useExportPromptVersion = () => {
  const userToken = useAuth()?.userToken;

  return useMutation<ExportPromptVersionResponse, Error, ExportPromptVersionRequest>({
    mutationFn: ({ workspaceId, promptRegistryId, versionNumber }) =>
      fetch(
        `${API_URL}/api/dashboard/v2/workspaces/${workspaceId}/prompt-registry-objects/${promptRegistryId}/prompt-versions/${versionNumber}/export`,
        { headers: jsonAuthHeaders(userToken!) },
      ).then(handleResponse),
    onSuccess: data => {
      const name = data.prompt.name.replace(/[/\\]/g, "_");
      downloadJson(data, `${name}-v${data.version.number}.json`);
    },
    onError: error => displayErrorToast(error),
  });
};
